import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import MetricCard from './components/MetricCard';
import NavigationTile from './components/NavigationTile';
import ActivityLog from './components/ActivityLog';
import QuickActions from './components/QuickActions';
import SystemStatus from './components/SystemStatus';
import Icon from '../../components/AppIcon';



import Button from '../../components/ui/Button';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const metrics = [
    {
      title: 'Aktif Rotalar',
      value: '24',
      subtitle: 'Toplam 28 rota',
      icon: 'Route',
      trend: 'up',
      trendValue: '+2',
      variant: 'primary'
    },
    {
      title: 'Bugünkü Seferler',
      value: '342',
      subtitle: 'Planlanan seferler',
      icon: 'Clock',
      trend: 'up',
      trendValue: '+18',
      variant: 'success'
    },
    {
      title: 'Toplam Duraklar',
      value: '156',
      subtitle: 'Tüm rotalarda',
      icon: 'MapPin',
      variant: 'default'
    },
    {
      title: 'Sistem Durumu',
      value: '99.8%',
      subtitle: 'Çalışma süresi',
      icon: 'Activity',
      trend: 'up',
      trendValue: '+0.2%',
      variant: 'success'
    }
  ];

  const navigationTiles = [
    {
      title: 'Rota Yönetimi',
      description: 'Rota bilgilerini ve güzergahları düzenle',
      icon: 'Route',
      path: '/routes-management',
      count: 12,
      badge: null
    },
    {
      title: 'Zamanlama',
      description: 'Sefer saatlerini ve sıklıklarını ayarla',
      icon: 'Clock',
      path: '/schedules-management',
      count: 48,
      badge: null
    },
    {
      title: 'Güzergah Geometrisi',
      description: 'Harita üzerinde rota çizimi ve düzenleme',
      icon: 'Map',
      path: '/route-geometry-management',
      count: null,
      badge: null
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: 'create',
      description: 'Yeni rota eklendi: Meram - Karatay Express',
      user: 'Ahmet Yılmaz',
      timestamp: new Date(Date.now() - 300000)
    },
    {
      id: 2,
      type: 'update',
      description: 'Selçuklu - Merkez rotası güncellendi',
      user: 'Ayşe Demir',
      timestamp: new Date(Date.now() - 900000)
    },
    {
      id: 3,
      type: 'create',
      description: '5 yeni durak eklendi',
      user: 'Mehmet Kaya',
      timestamp: new Date(Date.now() - 1800000)
    },
    {
      id: 4,
      type: 'update',
      description: 'Sabah seferleri programı güncellendi',
      user: 'Fatma Şahin',
      timestamp: new Date(Date.now() - 3600000)
    },
    {
      id: 5,
      type: 'delete',
      description: 'Kullanılmayan rota kaldırıldı',
      user: 'Ali Öztürk',
      timestamp: new Date(Date.now() - 7200000)
    }
  ];

  const systemServices = [
    {
      id: 1,
      name: 'API Sunucusu',
      icon: 'Server',
      status: 'operational',
      responseTime: 45
    },
    {
      id: 2,
      name: 'Veritabanı',
      icon: 'Database',
      status: 'operational',
      responseTime: 12
    },
    {
      id: 3,
      name: 'Tahmin Algoritması',
      icon: 'Cpu',
      status: 'operational',
      responseTime: 89
    },
    {
      id: 4,
      name: 'Harita Servisi',
      icon: 'Map',
      status: 'operational',
      responseTime: 156
    }
  ];

  const quickActions = [
    {
      label: 'Yeni Rota',
      icon: 'Plus',
      variant: 'primary',
      onClick: () => navigate('/routes-management')
    },
    {
      label: 'Zamanlama Ayarla',
      icon: 'Clock',
      variant: 'outline',
      onClick: () => navigate('/schedules-management')
    },
    {
      label: 'Harita Düzenle',
      icon: 'Map',
      variant: 'outline',
      onClick: () => navigate('/route-geometry-management')
    }
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuthToken');
    sessionStorage.removeItem('adminAuthExpiry');
    navigate('/admin-login');
  };

  const formatDateTime = (date) => {
    return date?.toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background">
        <AdminSidebar
          isCollapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          userName="Admin Kullanıcı"
          userRole="Sistem Yöneticisi"
        />

        <main className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} pt-20 lg:pt-4`}>
          <div className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    Kontrol Paneli
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Konya Dolmuş Sistemi Yönetimi
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
                    <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDateTime(currentTime)}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    iconName="LogOut"
                    iconPosition="left"
                    onClick={handleLogout}
                  >
                    Çıkış
                  </Button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
              {metrics?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            <div className="mb-6 md:mb-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Yönetim Panelleri</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                {navigationTiles?.map((tile, index) => (
                  <NavigationTile key={index} {...tile} />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
              <div className="lg:col-span-2">
                <ActivityLog activities={recentActivities} />
              </div>
              <div className="space-y-4 md:space-y-6">
                <SystemStatus services={systemServices} />
                <QuickActions actions={quickActions} />
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4 md:p-6">
              <div className="flex items-start gap-3">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg flex-shrink-0">
                  <Icon name="Info" size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-foreground mb-2">Sistem Bilgisi</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Bu panel, Konya dolmuş sisteminin gerçek zamanlı yönetimi için tasarlanmıştır. Tüm değişiklikler anında sisteme yansır ve kullanıcılar güncel bilgilere erişir.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 text-xs font-medium bg-success/10 text-success rounded-full">
                      Versiyon 1.0.0
                    </span>
                    <span className="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                      Son Güncelleme: 22 Ocak 2026
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
  );
};

export default AdminDashboard;